import type { PageSectionLayout } from '../services/sectionLayoutService';
import type { Track } from '../types/domain';
import { filterTracksSubscribedToGradeTrackSections } from './gradeTrackSectionSubscription';
import { localStorageSetItemWithQuotaRecovery } from './localStorageQuota';

/**
 * 편제 및 기준 시수(GradeTrackPage)에서 저장한 순서와 동일하게 편제명 배열을 만든다.
 * localStorage 키: `cp.trackOrder.${workspaceId}.${gradeId}`
 */
export function getOrderedTrackNamesForGrade(
  workspaceId: string,
  gradeId: string,
  allTracks: Track[],
  gradeTrackSections?: PageSectionLayout[] | null
): string[] {
  const scopedTracks =
    gradeTrackSections && gradeTrackSections.length > 0
      ? filterTracksSubscribedToGradeTrackSections(gradeTrackSections, gradeId, allTracks)
      : allTracks.filter((t) => (t.grade_id ?? '') === gradeId);
  const names = Array.from(
    new Set(
      scopedTracks
        .map((t) => (t.name ?? '').trim())
        .filter((n) => n.length > 0)
    )
  );
  const mergePreferred = (preferred: string[]): string[] => {
    const existing = preferred.map((n) => n.trim()).filter((n) => n.length > 0 && names.includes(n));
    const seen = new Set(existing);
    const additions = names.filter((n) => !seen.has(n));
    return [...existing, ...additions];
  };

  const sectionForGrade = gradeTrackSections?.find((s) => s.gradeIds.includes(gradeId));
  const layoutNames = (sectionForGrade?.trackColumns ?? [])
    .map((c) => c.name.trim())
    .filter((n) => n.length > 0);

  if (!workspaceId || !gradeId) {
    return layoutNames.length > 0 ? mergePreferred(layoutNames) : names;
  }
  try {
    const raw = window.localStorage.getItem(`cp.trackOrder.${workspaceId}.${gradeId}`);
    if (!raw) {
      return layoutNames.length > 0 ? mergePreferred(layoutNames) : names;
    }
    const stored = JSON.parse(raw) as unknown;
    if (!Array.isArray(stored)) {
      return layoutNames.length > 0 ? mergePreferred(layoutNames) : names;
    }
    const storedStr = stored.filter((n): n is string => typeof n === 'string');
    const preferred = layoutNames.length > 0 ? mergePreferred(layoutNames) : storedStr;
    return mergePreferred(preferred);
  } catch {
    return layoutNames.length > 0 ? mergePreferred(layoutNames) : names;
  }
}

export function saveTrackOrderForGrade(
  workspaceId: string,
  gradeId: string,
  orderedNames: string[]
): void {
  if (!workspaceId || !gradeId || typeof window === 'undefined') return;
  /** 실패 시 키 제거 → 다음 로드는 섹션 layout / DB 순서로 폴백 */
  localStorageSetItemWithQuotaRecovery(
    `cp.trackOrder.${workspaceId}.${gradeId}`,
    JSON.stringify(orderedNames)
  );
}
